import React from 'react';
import { ExternalLink, Eye, FileText, Link2, MoreHorizontal, Clock } from 'lucide-react';
import dayjs from 'dayjs';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ScoreRing,
  StatusPill,
  KeywordChip,
  Avatar,
  IssueCountBadge,
  deriveStatusVariant,
  RowActionButtons,
  type RowPrimaryAction,
} from '@/components/content-planner/shared';
import type { CalendarItem } from '@/types/contentPlanner';
import { universalBlogEditorPath } from '@/utils/editorRoutes';
import { cleanWpUrl } from '@/utils/wpUrl';
import { ModelColumnCell } from '@/components/BlogCreationCommandCenter/ModelColumnCell';
import { resolveRowModelAttribution } from '@/components/BlogCreationCommandCenter/modelAttribution';

// 9 columns: select · score · title · status · issues · keyword · author · performance · actions.
// (The previously-empty "secondary keywords" column was removed — see ContentListView header.)
// Issues column added in PR-2 to surface precomputed per-article issue counts.
export const LIST_GRID_TEMPLATE =
  '24px 40px minmax(0,2.4fr) 130px 48px minmax(0,1fr) 90px 110px auto';

export interface ContentListRowProps {
  item: CalendarItem;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onClick?: (item: CalendarItem) => void;
  getRowMenuItems?: (item: CalendarItem) => MenuProps['items'];
  getRowPrimaryActions?: (item: CalendarItem) => RowPrimaryAction[];
}

export const ContentListRow: React.FC<ContentListRowProps> = ({
  item,
  selected,
  onToggleSelect,
  onClick,
  getRowMenuItems,
  getRowPrimaryActions,
}) => {
  const navigate = useNavigate();
  const status = deriveStatusVariant(item);
  // "Not started yet" — a queued row with no draft (was the 'Idea' badge, which
  // now reads 'In Progress'; keep the behaviour keyed to the raw status).
  const isIdea = item.status === 'pending';
  const titleText = item.title || item.keyword || 'Untitled';
  const typeLabel = item.template_name || 'Blog Post';
  const modelAttribution = resolveRowModelAttribution(item);

  const defaultMenuItems: MenuProps['items'] = [
    {
      key: 'details',
      label: 'View Details',
      icon: <FileText size={14} />,
      onClick: () => navigate(universalBlogEditorPath(item.id)),
    },
    {
      key: 'editor',
      label: 'Open Editor',
      icon: <ExternalLink size={14} />,
      onClick: () => navigate(universalBlogEditorPath(item.id)),
    },
    ...(item.wp_url
      ? [
          {
            key: 'live',
            label: status === 'Published' ? 'Open Live Post' : 'Open Draft Preview',
            icon: status === 'Published' ? <Link2 size={14} /> : <Eye size={14} />,
            onClick: () => {
              const target =
                status === 'Published' ? cleanWpUrl(item.wp_url) ?? item.wp_url! : item.wp_url!;
              window.open(target, '_blank', 'noopener,noreferrer');
            },
          },
        ]
      : []),
  ];
  const menuItems = getRowMenuItems ? getRowMenuItems(item) : defaultMenuItems;

  const handleRowClick = () => onClick?.(item);
  const handleCheckboxClick = (e: React.MouseEvent | React.ChangeEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className="group"
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowClick();
        }
      }}
      style={{
        // relative so the hover action menu resolves to the row and can open
        // next to the cursor via --cp-reveal-left (see onMouseEnter below).
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: LIST_GRID_TEMPLATE,
        minWidth: 900,
        alignItems: 'center',
        gap: 14,
        padding: '14px 28px',
        borderBottom: '1px solid var(--color-warm-border)',
        background: selected ? 'var(--color-coral-light)' : 'var(--color-paper)',
        cursor: 'pointer',
        transition: 'background .12s',
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = 'var(--color-warm-bg)';
        // Anchor the action menu's LEFT edge just right of the cursor's x so it
        // opens beside where the mouse first entered the row (+12px so the
        // pointer isn't on top of the first option). Set once on enter — it
        // must not chase the cursor after the menu appears.
        const rect = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty(
          '--cp-reveal-left',
          `${Math.max(8, Math.round(e.clientX - rect.left) + 12)}px`,
        );
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = 'var(--color-paper)';
      }}
    >
      {/* Selection checkbox */}
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggleSelect(item.id)}
        onClick={handleCheckboxClick}
        aria-label={`Select ${titleText}`}
        style={{
          width: 14,
          height: 14,
          cursor: 'pointer',
          accentColor: 'var(--color-coral)',
        }}
      />

      {/* Score ring or dashed-circle for Idea / missing score */}
      {isIdea || item.quality_score == null ? (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            border: '1.5px dashed var(--color-warm-border-strong)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--color-ink-3)',
            fontSize: 12,
          }}
          aria-label="No score yet"
        >
          ?
        </div>
      ) : (
        <ScoreRing score={item.quality_score} />
      )}

      {/* Title + meta */}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--color-ink)',
            lineHeight: 1.3,
            marginBottom: 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            letterSpacing: '-0.005em',
          }}
          title={titleText}
        >
          {titleText}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 11,
            color: 'var(--color-ink-3)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <FileText size={12} strokeWidth={1.75} />
            <span style={{ fontWeight: 550 }}>{typeLabel}</span>
          </span>
          {item.word_count != null && item.word_count > 0 && (
            <>
              <span style={{ opacity: 0.5 }}>·</span>
              <span className="mono">{item.word_count.toLocaleString()} words</span>
            </>
          )}
          <span style={{ opacity: 0.5 }}>·</span>
          <ModelColumnCell {...modelAttribution} compact />
          {item.wp_url &&
            (status === 'Published' ? (
              <>
                <span style={{ opacity: 0.5 }}>·</span>
                <a
                  href={cleanWpUrl(item.wp_url) ?? item.wp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    color: 'var(--color-teal-dark)',
                  }}
                  title="View the live published page"
                >
                  <Link2 size={12} strokeWidth={1.75} />
                  live
                </a>
              </>
            ) : (
              <>
                <span style={{ opacity: 0.5 }}>·</span>
                <a
                  href={item.wp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    color: 'var(--color-ink-3)',
                  }}
                  title="Draft preview in WordPress — not published yet"
                >
                  <Eye size={12} strokeWidth={1.75} />
                  preview
                </a>
              </>
            ))}
        </div>
      </div>

      {/* Status pill */}
      <StatusPill status={status} />

      {/* Issue count — precomputed on the list payload (no N+1). */}
      <IssueCountBadge count={item.issue_count ?? null} compact />

      {/* Keyword (consolidated — secondary-keyword column removed; no such field on CalendarItem) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, minWidth: 0 }}>
        {item.keyword ? (
          <KeywordChip variant="primary" title={item.keyword}>
            {item.keyword}
          </KeywordChip>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--color-ink-3)' }}>—</span>
        )}
      </div>

      {/* Author — TODO(content-planner-redesign): no author field on CalendarItem; render Unassigned for all rows */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <Avatar name={null} title="Unassigned" />
        <span
          style={{
            fontSize: 11.5,
            color: 'var(--color-ink-3)',
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Unassigned
        </span>
      </div>

      {/* Performance / target date / em-dash */}
      <PerformanceCell item={item} status={status} />

      {/* Actions — prominent inline buttons + ⋯ overflow */}
      {getRowPrimaryActions ? (
        <RowActionButtons
          actions={getRowPrimaryActions(item)}
          menuItems={menuItems}
          revealOnHover
          anchorToCursor
        />
      ) : (
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            aria-label="Row actions"
            style={{
              display: 'grid',
              placeItems: 'center',
              width: 28,
              height: 28,
              borderRadius: 6,
              background: 'transparent',
              border: 'none',
              color: 'var(--color-ink-3)',
              cursor: 'pointer',
            }}
          >
            <MoreHorizontal size={16} strokeWidth={1.75} />
          </button>
        </Dropdown>
      )}
    </div>
  );
};

interface PerformanceCellProps {
  item: CalendarItem;
  status: ReturnType<typeof deriveStatusVariant>;
}

const PerformanceCell: React.FC<PerformanceCellProps> = ({ item, status }) => {
  const wrap: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
  };

  if (status === 'Published') {
    // TODO(content-planner-redesign): no traffic sparkline data — surface wp_url chip instead
    if (item.wp_url) {
      return (
        <div style={wrap}>
          <a
            href={cleanWpUrl(item.wp_url) ?? item.wp_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 11.5,
              color: 'var(--color-teal-dark)',
              fontWeight: 600,
            }}
          >
            <ExternalLink size={12} strokeWidth={1.75} />
            view post
          </a>
          <span style={{ fontSize: 10, color: 'var(--color-ink-3)' }}>published</span>
        </div>
      );
    }
    return (
      <div style={wrap}>
        <span style={{ fontSize: 11, color: 'var(--color-ink-3)' }}>—</span>
      </div>
    );
  }

  if (status === 'Scheduled' && item.scheduled_date) {
    return (
      <div style={wrap}>
        <span
          style={{
            fontSize: 11.5,
            color: 'var(--color-coral-dark)',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Clock size={12} strokeWidth={1.75} />
          {dayjs(item.scheduled_date).format('MMM D')}
        </span>
        <span style={{ fontSize: 10, color: 'var(--color-ink-3)' }}>scheduled</span>
      </div>
    );
  }

  if (item.scheduled_date) {
    return (
      <div style={wrap}>
        <span style={{ fontSize: 11.5, color: 'var(--color-ink-2)', fontWeight: 550 }}>
          {dayjs(item.scheduled_date).format('MMM D')}
        </span>
        <span style={{ fontSize: 10, color: 'var(--color-ink-3)' }}>target date</span>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <span style={{ fontSize: 11, color: 'var(--color-ink-3)' }}>—</span>
    </div>
  );
};

export default ContentListRow;
